import { useEffect, useMemo, useState } from "react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/atoms/select";
import { Card } from "@/components/atoms/card";
import SearchableSelect from "@/components/atoms/SearchableSelect";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/atoms/tabs";
import DriversDashboard from "@/pages/dashboard/partials/DriversDashboard";
import OrdersDashboard from "./partials/OrdersDashboard";
import { RefreshCw } from "lucide-react";
import { useOrdersApi } from "@/hooks/useOrdersApi";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/contexts/PermissionContext";
import { useBrandsApi } from "@/hooks/useBrandsApi";
import {
  useDashboardApi,
  DriversMetrics,
  DriversMetricsFilters,
} from "@/hooks/useDashboardApi";
import LoadingSpinner from "@/components/orders/LoadingSpinner";
import { useBranches } from "@/hooks/useBranches";

const periodOptions = [
  { value: "day", label: "Hoy" },
  { value: "week", label: "Esta Semana" },
  { value: "month", label: "Este Mes" },
];

export default function Dashboard() {
  const [period, setPeriod] = useState<"day" | "week" | "month">(
    () =>
      (localStorage.getItem("dashboard_period") as "day" | "week" | "month") ||
      "day",
  );
  const [brand, setBrand] = useState(() => "all");
  const [branch, setBranch] = useState(() => "all");

  // Estados para órdenes
  const [loading, setLoading] = useState(true);
  const [brandLoading, setBrandLoading] = useState(false);

  // Estados para drivers
  const [driversMetrics, setDriversMetrics] = useState<DriversMetrics | null>(
    null,
  );
  const [tabLoading, setTabLoading] = useState(false);

  // Estados para refresh
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Hooks
  const { fetchStateMetrics, stateMetrics, totalOrders, dailyBreakdown } =
    useOrdersApi();
  const { brands, fetchAllBrands } = useBrandsApi();
  const { branches, fetchBranches } = useBranches();
  const { fetchAllDriversMetrics } = useDashboardApi();
  const {
    userRole,
    user,
    isLoading: authLoading,
    userDataLoading,
    isManager,
  } = useAuth();
  const { hasBrandAccess } = usePermissions();

  // Mapeo de períodos para la API
  const mapPeriodForAPI = (period: "day" | "week" | "month") => {
    const periodMap: Record<string, string> = {
      day: "day",
      week: "week",
      month: "month",
    };
    return periodMap[period] || period;
  };

  // Fetch brands when auth is ready to avoid first-render errors
  useEffect(() => {
    // Calculate authentication readiness only once
    const authReady =
      !authLoading && !userDataLoading && (!isManager || !!user?.email);

    if (!authReady) {
      if (!loading) setLoading(true);
      return;
    }

    // If brands are already loaded, stop loading
    if (brands.length > 0) {
      if (loading) setLoading(false);
      return;
    }

    // Only fetch if not already loading to avoid duplicate fetches
    if (!loading) setLoading(true);

    // Fetch brands and branches together
    fetchAllBrands(user)
      .then(() => fetchBranches())
      .catch((error) => {
        console.error("Error fetching brands for dashboard:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [authLoading, userDataLoading, isManager, user, brands.length]);

  // Set default brand value based on permissions
  useEffect(() => {
    if (userRole && brands.length > 0) {
      let needsBrandUpdate = false;
      let newBrandValue = brand;

      if (userRole === "admin") {
        // Admin puede ver todas las brands
        // Si no hay marca seleccionada o no está en localStorage, establecer "all" por defecto
        if (!brand || brand === "") {
          needsBrandUpdate = true;
          newBrandValue = "all";
        }
      } else if (userRole === "manager") {
        // Manager solo ve las brands asignadas
        // Mantener "all" en el primer render y no forzar una marca específica
        const isCurrentBrandAccessible =
          brand !== "all" && brand !== "" && hasBrandAccess(parseInt(brand));

        if (!brand || brand === "") {
          // Si no hay valor, usar "all" por defecto
          needsBrandUpdate = true;
          newBrandValue = "all";
        } else if (
          brand !== "all" &&
          !isCurrentBrandAccessible &&
          brands.length > 0
        ) {
          // Solo si el valor actual no es "all" y no es accesible, seleccionar la primera accesible
          needsBrandUpdate = true;
          newBrandValue = String(brands[0].id);
        }
      } else {
        // Otros tipos de usuario - establecer "all" por defecto
        if (!brand || brand === "") {
          needsBrandUpdate = true;
          newBrandValue = "all";
        }
      }

      // Solo actualizar si es necesario para evitar bucles infinitos
      if (needsBrandUpdate && newBrandValue !== brand) {
        setBrand(newBrandValue);
      }
    }
  }, [userRole, brands.length, hasBrandAccess]);

  // Save filters to localStorage
  useEffect(() => {
    localStorage.setItem("dashboard_period", period);
    localStorage.setItem("dashboard_brand", brand);
    localStorage.setItem("dashboard_branch", branch);
  }, [period, brand, branch]);

  // Fetch filtered data when filters change
  useEffect(() => {
    if (!period || !brand || !branch) return;
    setTabLoading(true);

    const fetchFilteredData = async () => {
      try {
        setBrandLoading(true);
        setTabLoading(true);

        // Mapear período para la API
        const mappedPeriod = mapPeriodForAPI(period);

        // Fetch both orders and drivers data in parallel
        const [driversData] = await Promise.all([
          // Obtener métricas de drivers
          fetchDriversMetrics(brand, branch, period),
          // Obtener métricas de estado con filtros
          fetchStateMetrics(
            brand !== "all" ? brand : undefined,
            branch !== "all" ? branch : undefined,
            mappedPeriod as "day" | "week" | "month",
          ),
        ]);

        setDriversMetrics(driversData);
      } catch (error) {
        console.error("Error fetching dashboard metrics:", error);
      } finally {
        setBrandLoading(false);
        setTabLoading(false);
      }
    };

    fetchFilteredData();
  }, [brand, branch, period]);

  // Helper function to fetch drivers metrics
  const fetchDriversMetrics = async (
    brandFilter: string,
    branchFilter: string,
    periodFilter: "day" | "week" | "month",
  ) => {
    if (!branchFilter || !periodFilter || !brandFilter) {
      return null;
    }

    const filters: DriversMetricsFilters = {};
    if (branchFilter && branchFilter !== "all") {
      filters.branchId = branchFilter;
    }
    // Add brand filter if it's not "all"
    if (brandFilter && brandFilter !== "all") {
      filters.brandId = brandFilter;
    }
    filters.period = periodFilter;

    return await fetchAllDriversMetrics(filters);
  };

  // Reset branch effect removed; handled in onValueChange for brand

  // Transform brands data for select options (showing "all" only for admins)
  const brandOptions = [
    { value: "all", label: "Todas las marcas" },
    ...(brands?.map((brand) => ({
      value: String(brand.id),
      label: brand.name,
    })) || []),
  ];

  // Filter branches based on selected brand (similar to useOrderFilters logic)
  const filteredBranches = useMemo(() => {
    if (brand === "all") {
      return branches;
    }
    const brandId = parseInt(brand);
    if (Number.isNaN(brandId)) return branches;
    return branches.filter((b) => b.brand_id === brandId);
  }, [branches, brand]);

  // Reset branch if it no longer matches the selected brand
  useEffect(() => {
    if (brand !== "all" && branch !== "all") {
      const current = branches.find((b) => String(b.id) === branch);
      if (current && current.brand_id !== parseInt(brand)) {
        setBranch("all");
      }
    }
  }, [brand, branch, branches]);

  // Transform filtered branches for select options
  const branchOptions = [
    { value: "all", label: "Todas las sucursales" },
    ...(filteredBranches?.map((b) => ({
      value: String(b.id),
      label: b.name,
    })) || []),
  ];

  const handleReload = () => {
    setIsRefreshing(true);
    // Trigger refresh for all data
    const refreshData = async () => {
      setTabLoading(true);
      try {
        const mappedPeriod = mapPeriodForAPI(period);

        // Refresh all data in parallel
        const [driversData] = await Promise.all([
          // Refresh drivers metrics
          fetchDriversMetrics(brand, branch, period),
          // Refresh state metrics
          fetchStateMetrics(
            brand !== "all" ? brand : undefined,
            branch !== "all" ? branch : undefined,
            mappedPeriod as "day" | "week" | "month",
          ),
        ]);

        setDriversMetrics(driversData);
      } catch (error) {
        console.error("Error fetching dashboard metrics:", error);
      } finally {
        setIsRefreshing(false);
        setTimeout(() => {
          setTabLoading(false);
        }, 1000);
      }
    };

    refreshData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <Tabs defaultValue="ordenes" className="w-full">
      <TabsList className="bg-white w-full justify-start pl-6 gap-4">
        <TabsTrigger value="ordenes">Órdenes</TabsTrigger>
        <TabsTrigger value="conductores">Conductores</TabsTrigger>
      </TabsList>

      {/* Filters */}
      <div className="mb-4 px-4 md:px-6 mt-4 md:mt-6 max-w-7xl mx-auto">
        <Card className="p-4 bg-white">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            {/* Period Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Período
              </label>
              <Select
                value={period}
                onValueChange={(value) =>
                  setPeriod(value as "day" | "week" | "month")
                }
              >
                <SelectTrigger aria-label="Seleccionar periodo">
                  <SelectValue placeholder="Seleccionar periodo" />
                </SelectTrigger>
                <SelectContent>
                  {periodOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Brand Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Marca
              </label>
              <SearchableSelect
                options={brandOptions}
                value={brand}
                onValueChange={(value) => {
                  // Update brand and immediately reset branch to keep filters consistent
                  setBrand(value as string);
                  setBranch("all");
                }}
                placeholder="Seleccionar marca"
                searchPlaceholder="Buscar marca..."
                showAllOption={false}
                width="w-full"
              />
            </div>

            {/* Branch Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sucursal
              </label>
              <SearchableSelect
                options={branchOptions}
                value={branch}
                onValueChange={(value) => setBranch(value as string)}
                placeholder="Seleccionar sucursal"
                searchPlaceholder="Buscar sucursal..."
                showAllOption={false}
                width="w-full"
              />
            </div>

            {/* Refresh Button */}
            <div className="flex items-end w-full">
              <button
                type="button"
                className="w-full px-4 h-10 text-white border bg-indigo-600 rounded-md text-sm hover:bg-indigo-700 flex items-center justify-center gap-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleReload}
                disabled={isRefreshing || brandLoading}
              >
                <RefreshCw
                  className={`h-4 w-4 mr-2 ${isRefreshing || brandLoading ? "animate-spin duration-1000" : ""}`}
                />
                {isRefreshing || brandLoading
                  ? "Actualizando..."
                  : "Actualizar"}
              </button>
            </div>
          </div>
        </Card>
      </div>

      <TabsContent
        value="ordenes"
        className="px-4 md:px-6  pb-6 max-w-7xl mx-auto"
      >
        <OrdersDashboard
          loading={brandLoading || isRefreshing || tabLoading}
          stateMetrics={stateMetrics}
          dailyBreakdown={dailyBreakdown}
          totalOrders={totalOrders}
        />
      </TabsContent>

      <TabsContent
        value="conductores"
        className="px-4 md:px-6  pb-6 max-w-7xl mx-auto"
      >
        <DriversDashboard
          driversMetrics={driversMetrics}
          loading={tabLoading || isRefreshing || brandLoading}
          period={period}
        />
      </TabsContent>
    </Tabs>
  );
}
